import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

import {
  MpsAuditAggregateResult,
  MpsAuditHistoryEntry,
  MpsAuditHistoryFilters,
  MpsAuditHistoryResult,
} from '../models/mps-audit-history.model';
import { MPS_AUDIT_CONFIG } from '../mps-audit.config';

@Injectable({
  providedIn: 'root',
})
export class MpsAuditService {
  /** Default user-action tag emitted when none has been set. */
  public static readonly UNKNOWN_ACTION = 'unkown';

  /** Built-in event name emitted on every Angular router navigation. */
  public static readonly EVENT_PAGE_NAVIGATION = 'PAGE_NAVIGATION';

  /** Fixed `/mpsaudit` route group, optionally prefixed via config (e.g. '/api'). */
  private static readonly BASE_PATH = MPS_AUDIT_CONFIG.endpointPrefix + '/mpsaudit';
  private static readonly ENDPOINT_LOG_EVENT = MpsAuditService.BASE_PATH + '/log-event';
  private static readonly ENDPOINT_LOG_MESSAGE = MpsAuditService.BASE_PATH + '/log-message';
  private static readonly ENDPOINT_HISTORY = MpsAuditService.BASE_PATH + '/history';
  private static readonly ENDPOINT_HISTORY_EXPORT = MpsAuditService.BASE_PATH + '/history/export';
  private static readonly ENDPOINT_AGGREGATE = MpsAuditService.BASE_PATH + '/aggregate';

  private _previous_page: string | null = null;
  private _current_action: string = MpsAuditService.UNKNOWN_ACTION;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Auto-track Angular Router navigations as PAGE_NAVIGATION events.
    // Subclasses inherit this behavior unchanged; they can override
    // onNavigationEnd() if a project needs custom routing telemetry.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => this.onNavigationEnd(e));
  }

  /** Default page resolver. Override in a subclass for custom logic. */
  getCurrentPage(): string {
    return window.location.pathname;
  }

  /** Current user action tag forwarded to the back via the `mpsaudit-user-action` header. */
  getCurrentAction(): string {
    return this._current_action;
  }

  /** Set the current user-action tag (e.g. before triggering an HTTP call). */
  setCurrentAction(action: string): void {
    this._current_action = action;
  }

  /**
   * Default navigation telemetry. Emits PAGE_NAVIGATION with {previous_page, new_page}
   * on every successful Angular route change. Skips identical-URL re-navigations.
   * Marked `protected` so apps can override the emission rule without rewriting the subscription.
   */
  protected onNavigationEnd(event: NavigationEnd): void {
    const new_page = event.urlAfterRedirects;
    const previous_page = this._previous_page;

    if (previous_page === new_page) {
      return;
    }

    this.logEvent(MpsAuditService.EVENT_PAGE_NAVIGATION, { previous_page, new_page });
    this._previous_page = new_page;
  }

  public logEvent(eventName: string, data: unknown): void {
    if (!MPS_AUDIT_CONFIG.logEventEnabled) {
      return;
    }
    const body = { event: eventName, data };
    this.http
      .post<{ data: unknown }>(environment.api.host + MpsAuditService.ENDPOINT_LOG_EVENT, body)
      .pipe(map((response) => response.data))
      .subscribe({
        error: (err) => console.error('MpsAudit: logEvent failed', err),
      });
  }

  /**
   * Fetch the audit history of a business entity as structured JSON.
   * `type` is the back-end business_entity_type (e.g. 'App\\Models\\Grade').
   * `id` is optional — omit it to retrieve the history of every entity of that type.
   */
  public getBusinessHistory(
    type: string,
    id: number | string | null = null,
    filters: MpsAuditHistoryFilters = {}
  ): Observable<MpsAuditHistoryResult> {
    return this.http
      .get<{
        data: MpsAuditHistoryEntry[];
        meta?: { last_aggregated_at?: string | null };
      }>(environment.api.host + MpsAuditService.ENDPOINT_HISTORY, {
        params: this.buildHistoryParams(type, id, filters),
      })
      .pipe(
        map((response) => ({
          entries: response.data,
          lastAggregatedAt: response.meta?.last_aggregated_at ?? null,
        }))
      );
  }

  /**
   * Download the audit history of a business entity as a spreadsheet.
   * Streams the file from the back end and saves it client-side.
   */
  public exportBusinessHistory(
    type: string,
    id: number | string | null = null,
    filters: MpsAuditHistoryFilters & { fileName?: string } = {}
  ): Observable<Blob> {
    return this.http
      .get(environment.api.host + MpsAuditService.ENDPOINT_HISTORY_EXPORT, {
        params: this.buildHistoryParams(type, id, filters),
        responseType: 'blob',
      })
      .pipe(tap((blob) => saveAs(blob, filters.fileName ?? this.defaultHistoryFileName(type, id))));
  }

  /** Run the aggregation on demand (admin-only back route). */
  public runAggregate(): Observable<MpsAuditAggregateResult> {
    return this.http
      .get<{ data: MpsAuditAggregateResult }>(environment.api.host + MpsAuditService.ENDPOINT_AGGREGATE)
      .pipe(map((response) => response.data));
  }

  private buildHistoryParams(type: string, id: number | string | null, filters: MpsAuditHistoryFilters): HttpParams {
    let params = new HttpParams().set('type', type);
    if (id !== null && id !== undefined) {
      params = params.set('id', String(id));
    }
    if (filters.event && filters.event.length > 0) {
      for (const ev of filters.event) {
        params = params.append('event[]', ev);
      }
    }
    if (filters.email) {
      params = params.set('email', filters.email);
    }
    if (filters.name) {
      params = params.set('name', filters.name);
    }
    if (filters.fullname) {
      params = params.set('fullname', filters.fullname);
    }
    if (filters.entityId !== null && filters.entityId !== undefined && filters.entityId !== '') {
      params = params.set('entity_id', String(filters.entityId));
    }
    if (filters.from) {
      params = params.set('from', filters.from);
    }
    if (filters.to) {
      params = params.set('to', filters.to);
    }
    if (filters.limit) {
      params = params.set('limit', String(filters.limit));
    }
    return params;
  }

  private defaultHistoryFileName(type: string, id: number | string | null): string {
    const short = type.includes('\\') ? type.slice(type.lastIndexOf('\\') + 1) : type;
    const idPart = id !== null && id !== undefined ? `_${id}` : '_all';
    return `${short.toLowerCase()}${idPart}_history.xlsx`;
  }

  public logInfo(message: string, context: unknown = {}, channel: string | null = null): void {
    this.postLogMessage('info', message, context, channel).subscribe({
      error: (err) => console.error('MpsAudit: logInfo failed', err),
    });
  }

  public logError(message: string, context: unknown = {}, channel: string | null = null): void {
    this.postLogMessage('error', message, context, channel).subscribe({
      error: (err) => console.error('MpsAudit: logError failed', err),
    });
  }

  public logWarning(message: string, context: unknown = {}, channel: string | null = null): void {
    this.postLogMessage('warning', message, context, channel).subscribe({
      error: (err) => console.error('MpsAudit: logWarning failed', err),
    });
  }

  public logDebug(message: string, context: unknown = {}, channel: string | null = null): void {
    this.postLogMessage('debug', message, context, channel).subscribe({
      error: (err) => console.error('MpsAudit: logDebug failed', err),
    });
  }

  private postLogMessage(
    level: string,
    message: string,
    context: unknown,
    channel: string | null
  ): Observable<unknown> {
    const body = { level, message, context, channel };
    return this.http
      .post<{ data: unknown }>(environment.api.host + MpsAuditService.ENDPOINT_LOG_MESSAGE, body)
      .pipe(map((response) => response.data));
  }
}
